/**
 * Coque de l'application interne, la même pour tous les postes (décision du
 * 17/10/2026). À partir de 900 px : colonne latérale groupée et réductible.
 * En dessous : barre du haut épurée et barre du bas au pouce (Accueil,
 * Planning, Créer, un raccourci, Menu). Les rubriques viennent de
 * `useAreas`, filtrées par droits comme les routes.
 */
import { Suspense, useCallback, useMemo, useState, type ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useCan, useAuth } from '../../core/auth.js';
import { WelcomeScreen } from '../../features/onboarding/Welcome.js';
import { LocationIntro, PositionCompanion } from '../../features/profile/LocationAccess.js';
import { ActivityIndicator } from '../activity-indicator.js';
import { useQrScanner } from '../qr-scanner.js';
import { Spinner } from '../ui.js';
import { createActionsFor } from '../../app/create-actions.js';
import { useAreas, useBottomBar } from '../../app/use-areas.js';
import { BottomNav } from './BottomNav.js';
import { ShellChromeProvider } from './chrome.js';
import { CreateSheet } from './CreateMenu.js';
import { SearchDialog, useSearchShortcut } from './SearchDialog.js';
import { ShellBanners } from './ShellBanners.js';
import { SideNav, useNavCollapsed } from './SideNav.js';
import { TopBar } from './TopBar.js';
import { useProductFeedback } from './ProductFeedback.js';
import { ReportProblemContext } from '../ui/Feedback.js';

export function AppShell(): ReactNode {
  const location = useLocation();
  const { me } = useAuth();
  const can = useCan();
  const [collapsed, setCollapsed] = useNavCollapsed();
  const nav = useAreas();
  const bar = useBottomBar(nav);
  const scanner = useQrScanner();
  const [createOpen, setCreateOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  useSearchShortcut(openSearch);
  const modules = me?.enabled_modules;
  const actions = useMemo(() => createActionsFor({ can, modules: modules ?? [] }), [can, modules]);
  // Une erreur de chargement propose de la signaler à l'équipe MyEquip (avis, 24/10/2026).
  const feedback = useProductFeedback();
  const { open: openFeedback } = feedback;
  const reportProblem = useCallback(() => openFeedback('bug'), [openFeedback]);

  return (
    <ReportProblemContext.Provider value={reportProblem}>
    <ShellChromeProvider>
      <div className={`app-shell${collapsed ? ' app-shell--nav-reduite' : ''}`}>
        <SideNav
          sections={nav.sections}
          actions={actions}
          collapsed={collapsed}
          onCollapsedChange={setCollapsed}
          terrain={nav.terrain}
          onScan={scanner.open}
        />
        <div className="app-content">
          <ShellBanners />
          <WelcomeScreen />
          <LocationIntro />
          <PositionCompanion />
          <TopBar onSearch={openSearch} />
          <ActivityIndicator />
          <main className="app-main" id="contenu">
            <div key={location.pathname} className="route-transition">
              {/* A page still being downloaded keeps the shell: only the content
                  area shows the loader, the navigation stays usable. */}
              <Suspense fallback={<Spinner />}>
                <Outlet />
              </Suspense>
            </div>
          </main>
          <BottomNav bar={bar} createOpen={createOpen} onCreate={() => setCreateOpen(true)} />
        </div>
      </div>
      {createOpen ? (
        <CreateSheet actions={actions} terrain={nav.terrain} onClose={() => setCreateOpen(false)} onScan={scanner.open} />
      ) : null}
      {searchOpen ? (
        <SearchDialog areas={nav.areas} onClose={() => setSearchOpen(false)} onScan={scanner.open} />
      ) : null}
      {scanner.dialog}
      {feedback.sheet}
    </ShellChromeProvider>
    </ReportProblemContext.Provider>
  );
}
